import { lazy, Suspense, type ReactNode } from 'react';
import { Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import { Layout } from './components/Layout';
import { Loading } from './components/ui';
import { useAuth } from './lib/auth';
import LoginPage from './pages/LoginPage';
import InvitePage from './pages/InvitePage';
import ForgotPage from './pages/ForgotPage';
import DashboardPage from './pages/DashboardPage';
import NotFoundPage from './pages/NotFoundPage';

const ProjectsPage = lazy(() => import('./pages/ProjectsPage'));
const NewProjectPage = lazy(() => import('./pages/NewProjectPage'));
const ProjectDetailPage = lazy(() => import('./pages/ProjectDetailPage'));
const DesignEditorPage = lazy(() => import('./pages/DesignEditorPage'));
const SubmittalDetailPage = lazy(() => import('./pages/SubmittalDetailPage'));
const LeadsPage = lazy(() => import('./pages/LeadsPage'));
const FirmsPage = lazy(() => import('./pages/FirmsPage'));
const FirmDetailPage = lazy(() => import('./pages/FirmDetailPage'));
const ContactDetailPage = lazy(() => import('./pages/ContactDetailPage'));
const TasksPage = lazy(() => import('./pages/TasksPage'));
const SamplesPage = lazy(() => import('./pages/SamplesPage'));
const DocumentsPage = lazy(() => import('./pages/DocumentsPage'));
const GalleryPage = lazy(() => import('./pages/GalleryPage'));
const AccountPage = lazy(() => import('./pages/AccountPage'));
const ReviewQueuePage = lazy(() => import('./pages/staff/ReviewQueuePage'));
const RegistrationsPage = lazy(() => import('./pages/staff/RegistrationsPage'));
const SampleOrdersPage = lazy(() => import('./pages/staff/SampleOrdersPage'));
const GalleryApprovalsPage = lazy(() => import('./pages/staff/GalleryApprovalsPage'));
const UsersPage = lazy(() => import('./pages/admin/UsersPage'));
const NotificationsPage = lazy(() => import('./pages/admin/NotificationsPage'));
const CommissionsPage = lazy(() => import('./pages/CommissionsPage'));
const InquiriesPage = lazy(() => import('./pages/InquiriesPage'));
const InquirePage = lazy(() => import('./pages/InquirePage'));
const ClientProjectPage = lazy(() => import('./pages/client/ClientProjectPage'));
const ClientHubPage = lazy(() => import('./pages/client/ClientHubPage'));
const ClientSignInPage = lazy(() => import('./pages/client/ClientHubPage').then((m) => ({ default: m.ClientSignInPage })));
const ClientDesignPage = lazy(() => import('./pages/client/ClientDesignPage'));
const ClientPreviewPage = lazy(() => import('./pages/client/ClientDesignPage').then((m) => ({ default: m.ClientPreviewPage })));
const OrdersPage = lazy(() => import('./pages/staff/OrdersPage'));
const StaffCommissionsPage = lazy(() => import('./pages/staff/StaffCommissionsPage'));
const ReportsPage = lazy(() => import('./pages/staff/ReportsPage'));
const ImportPage = lazy(() => import('./pages/admin/ImportPage'));
const ErpSyncPage = lazy(() => import('./pages/admin/ErpSyncPage'));
const SystemPage = lazy(() => import('./pages/admin/SystemPage'));
const DriveSyncPage = lazy(() => import('./pages/admin/DriveSyncPage'));
const TemplatesPage = lazy(() => import('./pages/admin/TemplatesPage'));
const SampleCatalogPage = lazy(() => import('./pages/admin/SampleCatalogPage'));
const QuotesPage = lazy(() => import('./pages/staff/QuotesPage'));
const NewCustomQuotePage = lazy(() => import('./pages/staff/NewCustomQuotePage'));
const AssistantPage = lazy(() => import('./pages/staff/AssistantPage'));
const WorkshopLandingPage = lazy(() => import('./pages/WorkshopLandingPage'));
const DesignStartPage = lazy(() => import('./pages/DesignStartPage'));
const QuoteDetailPage = lazy(() => import('./pages/staff/QuoteDetailPage'));
const PrintPacksPage = lazy(() => import('./pages/staff/PrintPacksPage'));
const QuoteDesignPage = lazy(() => import('./pages/staff/QuoteDesignPage'));
const ClientAccessPage = lazy(() => import('./pages/staff/ClientAccessPage'));
const MeetTheTeamPage = lazy(() => import('./pages/MeetTheTeamPage'));

function RequireAuth({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <Loading />;
  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  return <>{children}</>;
}

/** True for a record id as the API stores it: a whole number from 1 to the largest Postgres int. */
const isRecordId = (v: string | undefined) => !!v && /^[1-9][0-9]{0,9}$/.test(v) && Number(v) <= 2_147_483_647;

/**
 * Shows the not-found screen for a malformed id in the address (such as /projects/abc), the same
 * screen a missing record gets, instead of asking the API for a record that cannot exist.
 */
function ValidIds({ names, children }: { names: string[]; children: ReactNode }) {
  const params = useParams();
  return names.every((n) => isRecordId(params[n])) ? <>{children}</> : <NotFoundPage />;
}

/** The design editor takes a design id, or "new" for a new design. */
function ValidDesignId({ children }: { children: ReactNode }) {
  const { designId } = useParams();
  return designId === 'new' || isRecordId(designId) ? <>{children}</> : <NotFoundPage />;
}

/** An emailed approval link from before client project pages opens the same token's project page. */
function ToClientPage() {
  const { token = '' } = useParams();
  return <Navigate to={`/client/p/${encodeURIComponent(token)}`} replace />;
}

function RequireStaff({ children }: { children: ReactNode }) {
  const { isStaff } = useAuth();
  return isStaff ? <>{children}</> : <NotFoundPage />;
}

function RequireAdmin({ children }: { children: ReactNode }) {
  const { isAdmin } = useAuth();
  return isAdmin ? <>{children}</> : <NotFoundPage />;
}

export default function App() {
  return (
    <Suspense fallback={<Loading />}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/invite/:token" element={<InvitePage />} />
        <Route path="/forgot" element={<ForgotPage />} />
        {/* Public pages: the website inquiry form and the client approval link. */}
        <Route path="/inquire" element={<InquirePage />} />
        <Route path="/workshop" element={<WorkshopLandingPage />} />
        {/* Client pages: no portal login. Old approval and share links open the client project page. */}
        <Route path="/approve/:token" element={<ToClientPage />} />
        <Route path="/share/:token" element={<ToClientPage />} />
        <Route path="/client" element={<ClientHubPage />} />
        <Route path="/client/signin/:token" element={<ClientSignInPage />} />
        <Route path="/client/p/:token" element={<ClientProjectPage />} />
        <Route path="/client/p/:token/design" element={<ClientDesignPage />} />
        <Route path="/client/p/:token/design/:designId" element={<ClientDesignPage />} />
        <Route path="/client/projects/:shareId" element={<ClientProjectPage />} />
        <Route path="/client/projects/:shareId/design" element={<ClientDesignPage />} />
        <Route path="/client/projects/:shareId/design/:designId" element={<ClientDesignPage />} />
        <Route path="/client/designs/:designId" element={<ClientDesignPage />} />
        <Route path="/client/quotes/:quoteId/design" element={<ClientDesignPage />} />
        {/* The configurator takes the whole screen. */}
        <Route
          path="/quotes/:id/design"
          element={
            <RequireAuth>
              <RequireStaff>
                <ValidIds names={['id']}>
                  <QuoteDesignPage />
                </ValidIds>
              </RequireStaff>
            </RequireAuth>
          }
        />
        {/* A design carried from the public Workshop by a rep or staff member who signed in (?from=workshop). */}
        <Route
          path="/designs/new"
          element={
            <RequireAuth>
              <DesignEditorPage />
            </RequireAuth>
          }
        />
        {/* Preview as client: a saved design as a client gets it, for staff or the project's seller. Nothing is saved. */}
        <Route
          path="/projects/:id/designs/:designId/preview"
          element={
            <RequireAuth>
              <ValidIds names={['id', 'designId']}>
                <ClientPreviewPage />
              </ValidIds>
            </RequireAuth>
          }
        />
        <Route
          path="/projects/:id/designs/:designId"
          element={
            <RequireAuth>
              <ValidIds names={['id']}>
                <ValidDesignId>
                  <DesignEditorPage />
                </ValidDesignId>
              </ValidIds>
            </RequireAuth>
          }
        />
        <Route
          element={
            <RequireAuth>
              <Layout />
            </RequireAuth>
          }
        >
          <Route index element={<DashboardPage />} />
          <Route path="projects" element={<ProjectsPage />} />
          <Route path="projects/new" element={<NewProjectPage />} />
          <Route path="projects/:id" element={<ValidIds names={['id']}><ProjectDetailPage /></ValidIds>} />
          <Route path="projects/:id/:tab" element={<ValidIds names={['id']}><ProjectDetailPage /></ValidIds>} />
          <Route path="design" element={<DesignStartPage />} />
          <Route path="projects/:id/submittals/:sid" element={<ValidIds names={['id', 'sid']}><SubmittalDetailPage /></ValidIds>} />
          <Route path="leads" element={<LeadsPage />} />
          <Route path="firms" element={<FirmsPage />} />
          <Route path="firms/:id" element={<ValidIds names={['id']}><FirmDetailPage /></ValidIds>} />
          <Route path="contacts/:id" element={<ValidIds names={['id']}><ContactDetailPage /></ValidIds>} />
          <Route path="tasks" element={<TasksPage />} />
          <Route path="samples" element={<SamplesPage />} />
          <Route path="documents" element={<DocumentsPage />} />
          <Route path="gallery" element={<GalleryPage />} />
          <Route path="gallery/album/:key" element={<GalleryPage />} />
          <Route path="account" element={<AccountPage />} />
          <Route path="commissions" element={<CommissionsPage />} />
          <Route path="inquiries" element={<InquiriesPage />} />
          <Route path="meet" element={<MeetTheTeamPage />} />
          <Route path="staff/orders" element={<RequireStaff><OrdersPage /></RequireStaff>} />
          <Route path="staff/commissions" element={<RequireStaff><StaffCommissionsPage /></RequireStaff>} />
          <Route path="staff/reports" element={<RequireStaff><ReportsPage /></RequireStaff>} />
          <Route path="admin/import" element={<RequireAdmin><ImportPage /></RequireAdmin>} />
          <Route path="admin/erp-sync" element={<RequireAdmin><ErpSyncPage /></RequireAdmin>} />
          <Route path="admin/system/*" element={<RequireAdmin><SystemPage /></RequireAdmin>} />
          <Route path="admin/drive-sync" element={<RequireAdmin><DriveSyncPage /></RequireAdmin>} />
          <Route path="staff/review" element={<RequireStaff><ReviewQueuePage /></RequireStaff>} />
          <Route path="quotes" element={<RequireStaff><QuotesPage /></RequireStaff>} />
          <Route path="quotes/custom" element={<RequireStaff><NewCustomQuotePage /></RequireStaff>} />
          <Route path="staff/assistant" element={<RequireStaff><AssistantPage /></RequireStaff>} />
          <Route path="quotes/:id" element={<RequireStaff><ValidIds names={['id']}><QuoteDetailPage /></ValidIds></RequireStaff>} />
          <Route path="staff/print-packs/:designId/:n" element={<RequireStaff><ValidIds names={['designId', 'n']}><PrintPacksPage /></ValidIds></RequireStaff>} />
          <Route path="staff/print-packs/quote/:quoteId" element={<RequireStaff><ValidIds names={['quoteId']}><PrintPacksPage /></ValidIds></RequireStaff>} />
          <Route path="staff/registrations" element={<RequireStaff><RegistrationsPage /></RequireStaff>} />
          <Route path="staff/samples" element={<RequireStaff><SampleOrdersPage /></RequireStaff>} />
          <Route path="staff/gallery" element={<RequireStaff><GalleryApprovalsPage /></RequireStaff>} />
          <Route path="staff/clients" element={<RequireStaff><ClientAccessPage /></RequireStaff>} />
          <Route path="admin/users" element={<RequireAdmin><UsersPage /></RequireAdmin>} />
          <Route path="admin/notifications" element={<RequireAdmin><NotificationsPage /></RequireAdmin>} />
          <Route path="admin/templates" element={<RequireAdmin><TemplatesPage /></RequireAdmin>} />
          <Route path="admin/samples" element={<RequireAdmin><SampleCatalogPage /></RequireAdmin>} />
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>
    </Suspense>
  );
}
